import { useLocation, useRouter } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { executeAppBack } from '~/services/AppBackService';
import { AppRoute } from '@agentselly/immodossier-shared';

interface Props {
  readonly fallbackTo?: AppRoute | string;
  readonly fallbackParams?: Record<string, string>;
  readonly fallbackHash?: string;
  readonly className?: string;
}

/**
 * BackButtonSidebarItem — Desktop-Seitenleisten-Äquivalent zum BackButton.
 *
 * Rendert einen klickbaren Button im Stil der Secondary-Sidebar-Items,
 * der executeAppBack aufruft (FromChain-basiertes Zurück) statt einer
 * statischen Route zu folgen.
 *
 * Wird in __root.tsx im propertyEditSubmenu-Bereich der Secondary-Sidebar
 * anstelle des früheren statischen MenuItemLink (→ dossier/home) verwendet.
 */
export function BackButtonSidebarItem({
  className,
  fallbackTo,
  fallbackParams,
  fallbackHash,
}: Props) {
  const router = useRouter();
  const location = useLocation();
  const { t } = useTranslate();

  const goBack = () => {
    executeAppBack(
      location,
      (options) => {
        void router.navigate(options);
      },
      {
        fallbackTo: fallbackTo as any,
        fallbackParams,
        fallbackHash,
      },
    );
  };

  return (
    <button
      type="button"
      onClick={goBack}
      className={clsx(
        'relative group w-full px-2 py-3 rounded-lg flex flex-col items-center gap-y-2 text-[11px] font-medium transition-colors',
        'text-gray-600 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-800/30 hover:text-primary-700 dark:hover:text-white',
        className,
      )}
      aria-label={t('back_to_overview')}
      title={t('back_to_overview')}
      data-testid="back-button-sidebar"
    >
      <ChevronLeftIcon
        className="h-6 w-6 text-gray-500 dark:text-gray-400 group-hover:text-primary-700 dark:group-hover:text-white transition-colors"
        aria-hidden="true"
      />
      <span className="text-center leading-tight">{t('back_to_overview')}</span>
    </button>
  );
}
